import "@/App.css";
import { BrowserRouter, Route, Routes, useLocation } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { ShopLayout } from "@/components/Shell";
import { ProtectedRoute } from "@/components/ProtectedRoute";

import Home, { Catalog } from "@/pages/Home";
import ProductDetail from "@/pages/ProductDetail";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import { Account, Orders } from "@/pages/Orders";
import { Login, Register } from "@/pages/Auth";
import AuthCallback from "@/pages/AuthCallback";

import AdminLayout from "@/pages/admin/AdminLayout";
import Dashboard from "@/pages/admin/Dashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import { AdminCategories, AdminZones } from "@/pages/admin/AdminConfig";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminStock from "@/pages/admin/AdminStock";
import { AdminCustomers, AdminLogs } from "@/pages/admin/AdminUsers";
import { AdminCommissions, AdminVendors } from "@/pages/admin/AdminVendors";
import { AdminCouriers, AdminSettings } from "@/pages/admin/AdminOps";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminPicking from "@/pages/admin/AdminPicking";
import AdminPickupCheck from "@/pages/admin/AdminPickupCheck";
import AdminReturns from "@/pages/admin/AdminReturns";
import AdminDeals from "@/pages/admin/AdminDeals";
import { AdminExports, AdminSms } from "@/pages/admin/AdminComms";
import { AdminReports, AdminTransfers } from "@/pages/admin/AdminLogistics";
import AdminPaymentReminders from "@/pages/admin/AdminPaymentReminders";
import CourierSpace from "@/pages/CourierSpace";
import VendorApply from "@/pages/VendorApply";
import VendorLayout from "@/pages/vendor/VendorLayout";
import { VendorDashboard, VendorFinances, VendorOrders, VendorProducts } from "@/pages/vendor/VendorPages";
import Receipt from "@/pages/Receipt";
import Professionals from "@/pages/Professionals";
import Deals from "@/pages/Deals";

const Shop = ({ children }) => <ShopLayout>{children}</ShopLayout>;

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;

  return (
    <Routes>
      <Route path="/" element={<Shop><Home /></Shop>} />
      <Route path="/catalogue" element={<Shop><Catalog /></Shop>} />
      <Route path="/offres" element={<Shop><Deals /></Shop>} />
      <Route path="/professionnels" element={<Shop><Professionals /></Shop>} />
      <Route path="/produit/:id" element={<Shop><ProductDetail /></Shop>} />
      <Route path="/panier" element={<Shop><Cart /></Shop>} />
      <Route path="/connexion" element={<Login />} />
      <Route path="/inscription" element={<Register />} />

      <Route
        path="/commander"
        element={
          <ProtectedRoute>
            <Shop><Checkout /></Shop>
          </ProtectedRoute>
        }
      />
      <Route
        path="/commandes"
        element={
          <ProtectedRoute>
            <Shop><Orders /></Shop>
          </ProtectedRoute>
        }
      />
      <Route
        path="/compte"
        element={
          <ProtectedRoute>
            <Shop><Account /></Shop>
          </ProtectedRoute>
        }
      />

      <Route
        path="/devenir-vendeur"
        element={
          <ProtectedRoute>
            <Shop><VendorApply /></Shop>
          </ProtectedRoute>
        }
      />
      <Route
        path="/commande/:id/bon-de-livraison"
        element={
          <ProtectedRoute>
            <Receipt />
          </ProtectedRoute>
        }
      />

      <Route
        path="/livreur"
        element={
          <ProtectedRoute roles={["courier"]}>
            <CourierSpace />
          </ProtectedRoute>
        }
      />

      <Route
        path="/vendeur"
        element={
          <ProtectedRoute roles={["vendor"]}>
            <VendorLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<VendorDashboard />} />
        <Route path="produits" element={<VendorProducts />} />
        <Route path="commandes" element={<VendorOrders />} />
        <Route path="finances" element={<VendorFinances />} />
      </Route>

      <Route
        path="/admin"
        element={
          <ProtectedRoute roles={["admin", "warehouse"]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<ProtectedRoute roles={["admin"]}><Dashboard /></ProtectedRoute>} />
        <Route path="produits" element={<ProtectedRoute roles={["admin"]}><AdminProducts /></ProtectedRoute>} />
        <Route path="offres" element={<ProtectedRoute roles={["admin"]}><AdminDeals /></ProtectedRoute>} />
        <Route path="categories" element={<ProtectedRoute roles={["admin"]}><AdminCategories /></ProtectedRoute>} />
        <Route path="commandes" element={<AdminOrders />} />
        <Route path="stock" element={<AdminStock />} />
        <Route path="preparation" element={<AdminPicking />} />
        <Route path="retraits" element={<AdminPickupCheck />} />
        <Route path="retours" element={<AdminReturns />} />
        <Route path="transferts" element={<AdminTransfers />} />
        <Route path="rapport-mensuel" element={<ProtectedRoute roles={["admin"]}><AdminReports /></ProtectedRoute>} />
        <Route path="relances" element={<ProtectedRoute roles={["admin"]}><AdminPaymentReminders /></ProtectedRoute>} />
        <Route path="exports" element={<AdminExports />} />
        <Route path="sms" element={<ProtectedRoute roles={["admin"]}><AdminSms /></ProtectedRoute>} />
        <Route path="clients" element={<ProtectedRoute roles={["admin"]}><AdminCustomers /></ProtectedRoute>} />
        <Route path="vendeurs" element={<ProtectedRoute roles={["admin"]}><AdminVendors /></ProtectedRoute>} />
        <Route path="livreurs" element={<AdminCouriers />} />
        <Route path="analyses" element={<ProtectedRoute roles={["admin"]}><AdminAnalytics /></ProtectedRoute>} />
        <Route path="parametres" element={<ProtectedRoute roles={["admin"]}><AdminSettings /></ProtectedRoute>} />
        <Route path="commissions" element={<ProtectedRoute roles={["admin"]}><AdminCommissions /></ProtectedRoute>} />
        <Route path="zones" element={<ProtectedRoute roles={["admin"]}><AdminZones /></ProtectedRoute>} />
        <Route path="journal" element={<ProtectedRoute roles={["admin"]}><AdminLogs /></ProtectedRoute>} />
      </Route>

      <Route
        path="*"
        element={
          <Shop>
            <div className="mx-auto max-w-lg px-4 py-24 text-center">
              <h1 className="font-display text-3xl font-bold text-slate-900">Page introuvable</h1>
              <p className="mt-2 text-sm text-slate-500">Cette page n'existe pas ou a été déplacée.</p>
            </div>
          </Shop>
        }
      />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <AppRouter />
          <Toaster position="top-center" richColors />
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
